import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import classnames from "classnames";
import { Button } from "antd";
import NGIDatePicker from "components/Common/inputs/NGIDatePicker";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
  },
  datePickerWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",

    "& .ant-picker": {
      width: "100%",
      maxWidth: 300,
      border: 0,
      background: "#F5F5F5 !important",
    },
  },
  buttonGroup: {
    display: "flex",
    gap: 3,
    justifyContent: "center",
    width: 300,
  },
  timeButton: {
    fontWeight: 300,
    borderRadius: 5,
    width: "50%",
  },
  selectedTimeButton: {
    backgroundColor: "#0085FF !important",
    color: "white !important",
  },
  disabledButton: {
    opacity: 0.5,
    pointerEvents: "none",
  },
  dateInput: {
    width: "100%",
  },
}));

export default function CalendarPicker({ className, limit = 14, selectedDate, setSelectedDate }) {
  const classes = useStyles();
  const [selectedDateValue, setSelectedDateValue] = useState(
    selectedDate?.availableAt ? moment(selectedDate?.availableAt) : null
  );
  const [selectedTime, setSelectedTime] = useState(selectedDate?.timeSlot || null);
  const now = moment();

  const minDate = now.hour() >= 19 ? moment().add(1, "day") : moment();
  const maxDate = moment(minDate).add(limit - 1, "days");

  const handleDateChange = useCallback(date => {
    if (date) {
      setSelectedDateValue(date);
      setSelectedTime(null);
      setSelectedDate({
        availableAt: date.format("YYYY-MM-DD"),
        timeSlot: null,
      });
    }
  }, []);

  const handleTimeSelect = useCallback(
    timeSlot => {
      if (selectedDateValue) {
        setSelectedTime(timeSlot);
        setSelectedDate({
          availableAt: selectedDateValue.format("YYYY-MM-DD"),
          timeSlot: timeSlot,
        });
      }
    },
    [selectedDateValue, setSelectedDate]
  );

  const isToday = useCallback(date => date?.isSame(now, "day"), [now]);

  const isMorningDisabled = useCallback(() => {
    if (!selectedDateValue) return true;
    if (!isToday(selectedDateValue)) return false;
    if (now.hour() >= 12) return true;
    return false;
  }, [selectedDateValue, now, isToday]);

  const isAfternoonDisabled = useCallback(() => {
    if (!selectedDateValue) return true;
    if (!isToday(selectedDateValue)) return false;
    if (now.hour() >= 19) return true;
    return false;
  }, [selectedDateValue, now, isToday]);

  const disabledDate = useCallback(
    date => date.isBefore(minDate, "day") || date.isAfter(maxDate, "day"),
    [minDate, maxDate]
  );

  useEffect(
    () => () => {
      setSelectedDate(null);
    },
    []
  );

  return (
    <div className={classnames(classes.container, className)}>
      <div className={classes.datePickerWrapper}>
        <NGIDatePicker
          value={selectedDateValue}
          onChange={handleDateChange}
          disableDates={disabledDate}
          format="DD/MM/YYYY"
          placeholder="Select date"
          className={classes.dateInput}
        />
      </div>

      <div className={classes.buttonGroup}>
        <Button
          variant="outlined"
          className={classnames(
            classes.timeButton,
            selectedTime === "Morning" && classes.selectedTimeButton,
            isMorningDisabled() && classes.disabledButton
          )}
          onClick={() => handleTimeSelect("Morning")}
          disabled={isMorningDisabled()}
        >
          MORNING
        </Button>
        <Button
          variant="outlined"
          className={classnames(
            classes.timeButton,
            selectedTime === "Afternoon" && classes.selectedTimeButton,
            isAfternoonDisabled() && classes.disabledButton
          )}
          onClick={() => handleTimeSelect("Afternoon")}
          disabled={isAfternoonDisabled()}
        >
          AFTERNOON
        </Button>
      </div>
    </div>
  );
}
